import React, { useEffect, useState } from "react";
import { Eye, Plus } from "lucide-react";
import { fetchApplicationUserPermission } from "../../../Apis/ApplicationManagementApis";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";

const AVATAR_COLORS = [
  "#e8711a", "#d64343", "#7a59c2", "#0d8f6f", "#3f6fd6",
  "#c27a10", "#0e8fa8", "#8f5c0e", "#2f7a3f", "#a83f72",
];

function colorFromStr(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function userInitials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return String(name || "").slice(0, 2).toUpperCase();
}

function UserPermissionList() {
  const [assignment, setAssignment] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData } = useAuthStore();
  const { Permission } = usePermissionDataStore();
  const org = userData?.organization;
  const navigate = useNavigate();

  const canAdd = Array.isArray(Permission) && Permission.includes("USER_PERMISSION_ADD");
  const canEdit = Array.isArray(Permission) && Permission.includes("USER_PERMISSION_EDIT");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetchApplicationUserPermission(org?.organization_id);
        const permissions = res?.permissions || [];

        const grouped = permissions.reduce((acc, curr) => {
          const userId = curr.application_user_id;
          if (!acc[userId]) {
            acc[userId] = {
              id: userId,
              user_id: userId,
              user_name: curr.application_user?.full_name || "Unknown User",
              email: curr.application_user?.email || "",
            };
          }
          return acc;
        }, {});

        setAssignment(Object.values(grouped));
      } catch (error) {
        console.error("Error fetching user permissions:", error);
        toast.error("Failed to load user permissions");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [org?.organization_id]);

  const deleteassignment = async (userId) => {
    try {
      const response = await axios.delete(
        `${MAIN_URL}/api/application/user-permissions/particular/${userId}`,
        { params: { organization_id: org?.organization_id } }
      );
      if (response.status === 200) {
        toast.success("User permissions deleted successfully");
        setAssignment((prev) => prev.filter((item) => item.user_id !== userId));
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.error || "Failed to delete user permissions");
    }
  };

  return (
    <div className="ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">User Permission Overrides</h1>
          <div className="ems-page-sub">
            <span>User management</span>
            <span className="ems-dot" />
            <span>{assignment.length} users with overrides</span>
          </div>
        </div>
        <div className="ems-head-actions">
          {canAdd && (
            <button className="ems-btn ems-primary" onClick={() => navigate("/application/user-permission/add")}>
              <Plus size={16} />
              Add User Permission
            </button>
          )}
        </div>
      </div>

      <div className="ems-section-divider" />

      <div className="ems-card ems-table-card">
        {loading ? (
          <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ems-faint)" }}>
            Loading…
          </div>
        ) : assignment.length === 0 ? (
          <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ems-faint)", fontSize: 14 }}>
            No user permission overrides found.
          </div>
        ) : (
          <div className="ems-table-wrap">
            <table className="ems-data">
              <thead>
                <tr>
                  <th>User Name</th>
                  <th>Email</th>
                  <th style={{ width: 200 }}>View Permissions</th>
                  <th className="ems-num" style={{ width: 110 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {assignment.map((item) => {
                  const initials = userInitials(item.user_name);
                  const avatarColor = colorFromStr(item.user_name);
                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="ems-act-cell">
                          <span
                            className="ems-t-avatar"
                            style={{ background: avatarColor, width: 36, height: 36, borderRadius: 99, fontSize: 13 }}
                          >
                            {initials}
                          </span>
                          <span className="ems-lead-name">{item.user_name}</span>
                        </div>
                      </td>
                      <td>
                        {item.email ? (
                          <a className="ems-email" href={`mailto:${item.email}`} onClick={(e) => e.stopPropagation()}>
                            {item.email}
                          </a>
                        ) : (
                          <span style={{ color: "var(--ems-faint)" }}>—</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="ems-btn-view"
                          onClick={() => navigate(`/application/user-permission/view/${item.user_id}`)}
                        >
                          <Eye size={15} />
                          View
                        </button>
                      </td>
                      <td className="ems-num">
                        {canEdit && (
                          <button
                            className="ems-link-edit"
                            onClick={() => navigate(`/application/user-permission/edit/${item.user_id}`)}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserPermissionList;
